import {Fragment} from "react";
import {css} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {PercentIcon} from "@pg-design/icons-module";
import {Link} from "@pg-design/link-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {ApplicationModalButton} from "../../../application/components/ApplicationModalButton";
import {ApplicationFormPromotionPill} from "../../../application/components/form_box/ApplicationFormPromotionPill";
import {GTMEventApplicationSourceSection} from "../../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/form/application_form_source";
import {OfferDetailViewHeader} from "../../../atoms/OfferDetailViewHeader";
import {IBoosterClickHandlerParams} from "../../../boosters/types/IBoosterClickHandlerParams";
import {useShouldHidePrices} from "../../../hooks/use_should_hide_prices";
import {useShouldShowPriceByOfferConfigAndUser} from "../../../hooks/use_should_show_price_by_offer_config_and_user";
import {IOfferDetailPromotion} from "../../detail/actions/fetch_offer_detail_promotions";
import {OfferDisplayType} from "../../helpers/OfferDisplayType";
import {getOfferTypeNamePlural} from "../../helpers/OfferType";
import {IOfferDetail} from "../../types/IOfferDetail";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {isOfferArchived} from "../../utils/is_offer_archived";
import {OfferDetailHeaderHiddenPriceVariantPrice} from "./OfferDetailHeaderHiddenPriceVariantPrice";

interface IProps {
    offer: IOfferDetail;
    promotions: IOfferDetailPromotion[] | null;
    onBoosterClick: (params: IBoosterClickHandlerParams) => void;
    onLocationClick: () => void;
    variant: "visible" | "blur";
}

export const OfferDetailHeaderHiddenPriceVariant = (props: IProps) => {
    const {offer} = props;

    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const isPreSale = !!offer.configuration.pre_sale;
    const isPlanned = offer.configuration.display_type === OfferDisplayType.PLANNED;
    const isArchived = isOfferArchived(offer);
    const isInvestmentApartment = offer.slogan && offer.slogan === "Apartamenty inwestycyjne";

    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: offer.vendor.slug, vendorId: offer.vendor.id});

    // Skip auth check for "price from" visibility (SEO requirement)
    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id,
        skipAuthCheck: true
    });

    const {shouldHidePrices} = useShouldHidePrices();

    const gtmDataVisible = {
        clickText: "Zapytaj o ofertę",
        source: GTMEventApplicationSourceSection.HEADER_LEAD,
        vendorName: offer.vendor.name ?? offer.vendor.id.toString(),
        offerName: offer.name ?? offer.id.toString(),
        gtmSourceSection: GTMEventApplicationSourceSection.HEADER_LEAD
    };

    const offerTypePlural = isApartment || isInvestmentApartment ? "apartamenty" : getOfferTypeNamePlural(offer.type);

    const variant = shouldShowPrice ? "visible" : props.variant;

    return (
        <div className={wrapperStyle} data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_HEADER_SECTION}>
            <OfferDetailViewHeader
                mainContentClassName={mainContentStyle}
                mainHeadlineClassName={mainHeadlineTextStyle}
                address={offer.address}
                badges={
                    isArchived || isPreSale || isPlanned ? (
                        <Fragment>
                            {isArchived && <Badge variant="label_secondary">Oferta archiwalna</Badge>}

                            {isPreSale && (
                                <Badge variant="label_success">
                                    <strong>Przedsprzedaż</strong>
                                </Badge>
                            )}

                            {isPlanned && (
                                <Badge variant="label_success" icon={PercentIcon}>
                                    Inwestycja planowana
                                </Badge>
                            )}
                        </Fragment>
                    ) : null
                }
                cta={
                    <div className={ctaWrapperStyle}>
                        <div className={ctaContainerStyle}>
                            <div className={priceContainerStyle}>
                                <OfferDetailHeaderHiddenPriceVariantPrice offer={offer} variant={variant} />
                            </div>

                            {!shouldHidePrices && (
                                <ApplicationModalButton
                                    source={ApplicationSource.Offer}
                                    sourceSection={ApplicationSourceSection.MODAL}
                                    vendorId={offer.vendor.id}
                                    offerId={offer.id}
                                    propertyId={null}
                                    currency={offer.currency}
                                    forceClose={getOfferDisplayStatus(offer) === "limited"}
                                    render={({openModal}) => (
                                        <Button
                                            className={buttonStyle}
                                            size="medium"
                                            variant="outlined_secondary"
                                            onClick={() => openModal(gtmDataVisible)}
                                            dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_LEAD_FORM_HEADING}
                                        >
                                            Zapytaj o ofertę
                                        </Button>
                                    )}
                                />
                            )}
                        </div>

                        <ApplicationFormPromotionPill />
                    </div>
                }
                ctaClassName={ctaSectionStyle}
                externalLinks={
                    <h2 className={css({m: 0})} data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_HEADER_VENDOR_NAME}>
                        <Text variant="info_txt_1" className={subtitleStyle}>
                            {`${offerTypePlural.charAt(0).toUpperCase() + offerTypePlural.slice(1)} na sprzedaż oferowane bezpośrednio przez `}
                            {offer.investor ? (
                                <Text as="span" variant="info_txt_1" className={vendorNameStyle}>
                                    {offer.investor}
                                </Text>
                            ) : (
                                <Link underline href={vendorLink} size="inherit" color="var(--colors-gray-700)">
                                    {offer.vendor.name}
                                </Link>
                            )}
                        </Text>
                    </h2>
                }
                onLocationClick={props.onLocationClick}
                title={offer.name}
                slogan={offer.slogan}
            />
        </div>
    );
};

const wrapperStyle = css({
    display: "flex",
    position: "relative",
    flexWrap: "wrap",
    marginBottom: 2,

    md: {
        marginBottom: 0
    }
});

const mainContentStyle = css({
    flexDirection: "column",
    md: {
        width: "100%",
        flexWrap: "nowrap",
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between"
    }
});

const mainHeadlineTextStyle = css({
    md: {
        alignSelf: "center"
    }
});

const subtitleStyle = css({
    color: "var(--colors-gray-700)",
    display: "inline"
});

const vendorNameStyle = css({
    color: "var(--colors-gray-700)"
});

const ctaSectionStyle = css({
    display: "flex",
    gap: 2,
    flexDirection: "column",
    width: "100%",
    flex: "0 0 auto",
    md: {
        width: "auto",
        flexDirection: "row",
        alignItems: "center",
        gap: 3
    }
});

const ctaWrapperStyle = css({
    display: "flex",
    flexDirection: "column"
});

const ctaContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    mb: 2,
    gap: 2,
    md: {
        flexDirection: "row",
        gap: 3
    }
});

const priceContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end"
});

const buttonStyle = css({
    px: 4
});
import {Fragment} from "react";
import {css} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {PercentIcon} from "@pg-design/icons-module";
import {Link} from "@pg-design/link-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {ApplicationModalButton} from "../../../application/components/ApplicationModalButton";
import {ApplicationFormPromotionPill} from "../../../application/components/form_box/ApplicationFormPromotionPill";
import {GTMEventApplicationSourceSection} from "../../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/form/application_form_source";
import {OfferDetailViewHeader} from "../../../atoms/OfferDetailViewHeader";
import {IBoosterClickHandlerParams} from "../../../boosters/types/IBoosterClickHandlerParams";
import {useShouldHidePrices} from "../../../hooks/use_should_hide_prices";
import {useShouldShowPriceByOfferConfigAndUser} from "../../../hooks/use_should_show_price_by_offer_config_and_user";
import {IOfferDetailPromotion} from "../../detail/actions/fetch_offer_detail_promotions";
import {OfferDisplayType} from "../../helpers/OfferDisplayType";
import {getOfferTypeNamePlural} from "../../helpers/OfferType";
import {IOfferDetail} from "../../types/IOfferDetail";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {isOfferArchived} from "../../utils/is_offer_archived";
import {OfferDetailHeaderHiddenPriceVariantPrice} from "./OfferDetailHeaderHiddenPriceVariantPrice";

interface IProps {
    offer: IOfferDetail;
    promotions: IOfferDetailPromotion[] | null;
    onBoosterClick: (params: IBoosterClickHandlerParams) => void;
    onLocationClick: () => void;
    variant: "visible" | "blur";
}

export const OfferDetailHeaderHiddenPriceVariant = (props: IProps) => {
    const {offer} = props;

    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const isPreSale = !!offer.configuration.pre_sale;
    const isPlanned = offer.configuration.display_type === OfferDisplayType.PLANNED;
    const isArchived = isOfferArchived(offer);
    const isInvestmentApartment = offer.slogan && offer.slogan === "Apartamenty inwestycyjne";

    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: offer.vendor.slug, vendorId: offer.vendor.id});

    // Skip auth check for "price from" visibility (SEO requirement)
    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id,
        skipAuthCheck: true
    });

    const {shouldHidePrices} = useShouldHidePrices();

    const gtmDataVisible = {
        clickText: "Zapytaj o ofertę",
        source: GTMEventApplicationSourceSection.HEADER_LEAD,
        vendorName: offer.vendor.name ?? offer.vendor.id.toString(),
        offerName: offer.name ?? offer.id.toString(),
        gtmSourceSection: GTMEventApplicationSourceSection.HEADER_LEAD
    };

    const offerTypePlural = isApartment || isInvestmentApartment ? "apartamenty" : getOfferTypeNamePlural(offer.type);

    const variant = shouldShowPrice ? "visible" : props.variant;

    return (
        <div className={wrapperStyle} data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_HEADER_SECTION}>
            <OfferDetailViewHeader
                mainContentClassName={mainContentStyle}
                mainHeadlineClassName={mainHeadlineTextStyle}
                address={offer.address}
                badges={
                    isArchived || isPreSale || isPlanned ? (
                        <Fragment>
                            {isArchived && <Badge variant="label_secondary">Oferta archiwalna</Badge>}

                            {isPreSale && (
                                <Badge variant="label_success">
                                    <strong>Przedsprzedaż</strong>
                                </Badge>
                            )}

                            {isPlanned && (
                                <Badge variant="label_success" icon={PercentIcon}>
                                    Inwestycja planowana
                                </Badge>
                            )}
                        </Fragment>
                    ) : null
                }
                cta={
                    <div className={ctaWrapperStyle}>
                        <div className={ctaContainerStyle}>
                            <div className={priceContainerStyle}>
                                <OfferDetailHeaderHiddenPriceVariantPrice offer={offer} variant={variant} />
                            </div>

                            {!shouldHidePrices && (
                                <ApplicationModalButton
                                    source={ApplicationSource.Offer}
                                    sourceSection={ApplicationSourceSection.MODAL}
                                    vendorId={offer.vendor.id}
                                    offerId={offer.id}
                                    propertyId={null}
                                    currency={offer.currency}
                                    forceClose={getOfferDisplayStatus(offer) === "limited"}
                                    render={({openModal}) => (
                                        <Button
                                            className={buttonStyle}
                                            size="medium"
                                            variant="outlined_secondary"
                                            onClick={() => openModal(gtmDataVisible)}
                                            dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_LEAD_FORM_HEADING}
                                        >
                                            Zapytaj o ofertę
                                        </Button>
                                    )}
                                />
                            )}
                        </div>

                        <ApplicationFormPromotionPill />
                    </div>
                }
                ctaClassName={ctaSectionStyle}
                externalLinks={
                    <h2 className={css({m: 0})} data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_HEADER_VENDOR_NAME}>
                        <Text variant="info_txt_1" className={subtitleStyle}>
                            {`${offerTypePlural.charAt(0).toUpperCase() + offerTypePlural.slice(1)} na sprzedaż oferowane bezpośrednio przez `}
                            {offer.investor ? (
                                <Text as="span" variant="info_txt_1" className={vendorNameStyle}>
                                    {offer.investor}
                                </Text>
                            ) : (
                                <Link underline href={vendorLink} size="inherit" color="var(--colors-gray-700)">
                                    {offer.vendor.name}
                                </Link>
                            )}
                        </Text>
                    </h2>
                }
                onLocationClick={props.onLocationClick}
                title={offer.name}
                slogan={offer.slogan}
            />
        </div>
    );
};

const wrapperStyle = css({
    display: "flex",
    position: "relative",
    flexWrap: "wrap",
    marginBottom: 2,

    md: {
        marginBottom: 0
    }
});

const mainContentStyle = css({
    flexDirection: "column",
    md: {
        width: "100%",
        flexWrap: "nowrap",
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between"
    }
});

const mainHeadlineTextStyle = css({
    md: {
        alignSelf: "center"
    }
});

const subtitleStyle = css({
    color: "var(--colors-gray-700)",
    display: "inline"
});

const vendorNameStyle = css({
    color: "var(--colors-gray-700)"
});

const ctaSectionStyle = css({
    display: "flex",
    gap: 2,
    flexDirection: "column",
    width: "100%",
    flex: "0 0 auto",
    md: {
        width: "auto",
        flexDirection: "row",
        alignItems: "center",
        gap: 3
    }
});

const ctaWrapperStyle = css({
    display: "flex",
    flexDirection: "column"
});

const ctaContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    mb: 2,
    gap: 2,
    md: {
        flexDirection: "row",
        gap: 3
    }
});

const priceContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end"
});

const buttonStyle = css({
    px: 4
});
